import { ref, watch } from "vue";
import { useIncidentHub } from "@/composables/useIncidentHub";
import { MediaService } from "@/services/MediaService";
import { FacingMode } from "@/enums/FacingMode";
import { useVideoCall, videoState } from "@/composables/useVideoCall";
import { useSnapshot } from "@/composables/useSnapshot";
import type { Incident } from "@/types";
import { WebAppState } from "@/enums/WebAppState";
import { VideoState } from "@/types/VideoState";

const cameraStream = ref<MediaStream | null>(null);
const currentFacingMode = ref<FacingMode>(FacingMode.Environment);

const wasActivated = ref(false);
const cameraWasEverGranted = ref(false);
const videoId = ref<string | null>(null);

const oldVideoId = ref<string | null>(null);
const oldFacingMode = ref<FacingMode>();

let videoCall: ReturnType<typeof useVideoCall> | null = null;
let watchersRegistered = false;

export function useVideo() {
  const { sendSnapshot } = useSnapshot();
  const incidentHub = useIncidentHub();
  const mediaService = new MediaService(sendSnapshot);

  async function loadVideo(incident: Incident) {
    const connection = await incidentHub.startSignalRConnection();

    connection.on("LiveVideoStarted", async (incident: { id: string }, liveVideo: { id: string }) => {
      videoId.value = liveVideo.id;
    });

    connection.on("LiveVideoEnded", async () => {
      await stopVideo();
    });

    connection.on("SwitchCameraRequest", () => {
      switchFacingMode();
    });

    connection.on("TakeCallerCameraSnapshot", () => {
      mediaService.takeSnapshot2();
    });

    if (incident.liveVideo) {
      wasActivated.value = true;
      videoId.value = incident.liveVideo.id;
    }
  }

  //TODO: spravne vybirani kamery na vsech zarizenich
  function switchFacingMode() {
    currentFacingMode.value =
      currentFacingMode.value === FacingMode.Environment
        ? FacingMode.User
        : FacingMode.Environment;
  }

  if (!watchersRegistered) {
    watchersRegistered = true;

    watch(currentFacingMode, async (newFacingMode) => {
      console.log("Current facing mode changed:", newFacingMode, oldFacingMode.value);

      if (!newFacingMode || newFacingMode === oldFacingMode.value) {
        return;
      }

      const currentVideoTrack = cameraStream.value?.getVideoTracks()[0];
      if (currentVideoTrack) {
        currentVideoTrack.stop();
      }

      incidentHub.setWebAppState(WebAppState.CameraPermissionRequested);
      cameraStream.value = await mediaService.getCameraLocalStream(newFacingMode);
      incidentHub.setWebAppState(cameraStream.value ? WebAppState.CameraPermissionGranted : WebAppState.CameraPermissionRejected);

      const track = cameraStream.value?.getVideoTracks()[0];
      if (track && videoCall?.isActive()) {
        await videoCall.replaceVideoTrack(track);
        incidentHub.setWebAppState(WebAppState.VideoTransmissionActive);
      }

      oldFacingMode.value = newFacingMode;
    });

    watch(videoId, async (newVideoId) => {
      await connectToVideo(newVideoId);
    });

    watch(videoState, async (newState) => {
      console.log("Video state changed:", newState);
      if (newState === VideoState.UnsupportedBrowser) {
        incidentHub.setWebAppState(WebAppState.UnsupportedBrowser);
      }
    });
  }

  async function connectToVideo(newVideoId: string | null) {
    if (!newVideoId || newVideoId === oldVideoId.value) {
      return;
    }

    console.log("Video ID changed:", newVideoId);

    const oldId = oldVideoId.value;

    oldVideoId.value = newVideoId;

    if (oldId) {
      await stopVideo();
    }

    await startVideo(newVideoId);
  }

  async function startVideo(currentVideoId: string) {
    wasActivated.value = true;

    try {
      if (!cameraStream.value) {
        incidentHub.setWebAppState(WebAppState.CameraPermissionRequested);
        cameraStream.value =
          await mediaService.getCameraLocalStream(currentFacingMode.value);
        incidentHub.setWebAppState(cameraStream.value ? WebAppState.CameraPermissionGranted : WebAppState.CameraPermissionRejected);
      }

      const videoTrack = cameraStream.value?.getVideoTracks()[0];

      if (!videoTrack) {
        console.warn("Camera not available — video UI shown without camera stream");
        cameraStream.value = null;
        return;
      }

      cameraWasEverGranted.value = true;

      incidentHub.setWebAppState(WebAppState.StartingVideoTransmission);

      videoCall = useVideoCall(currentVideoId);
      await videoCall.startCall(videoTrack);

      incidentHub.setWebAppState(WebAppState.VideoTransmissionActive);
    } catch (err) {
      console.error("startVideo failed", err);
      incidentHub.setWebAppState(WebAppState.VideoTransmissionFailed);
    }
  }

  async function startCamera(): Promise<boolean> {
    try {
      incidentHub.setWebAppState(WebAppState.CameraPermissionRequested);
      cameraStream.value =
        await mediaService.getCameraLocalStream(currentFacingMode.value);

      const track = cameraStream.value?.getVideoTracks()[0];
      if (!track) {
        console.warn("startCamera: camera access denied or unavailable");
        cameraStream.value = null;
        incidentHub.setWebAppState(WebAppState.CameraPermissionRejected);
        return false;
      }

      incidentHub.setWebAppState(WebAppState.CameraPermissionGranted);
      cameraWasEverGranted.value = true;

      if (videoCall?.isActive()) {
        // Video call already exists with an active producer — replace track and resume
        await videoCall.replaceVideoTrack(track);
        await videoCall.resumeProducer();
      } else if (videoId.value) {
        incidentHub.setWebAppState(WebAppState.StartingVideoTransmission);
        // No video call yet, or previous instance was cleaned up — create a new one
        videoCall = useVideoCall(videoId.value);
        await videoCall.startCall(track);
        incidentHub.setWebAppState(WebAppState.VideoTransmissionActive);
      }

      return true;
    } catch (err) {
      console.error("startCamera failed", err);
      incidentHub.setWebAppState(WebAppState.VideoTransmissionFailed);
      return false;
    }
  }

  async function stopCamera() {
    try {
      if (videoCall?.isActive()) {
        videoCall.setIntentionalTrackStop(true);
        await videoCall.pauseProducer();
      }

      cameraStream.value?.getTracks().forEach(t => t.stop());
      cameraStream.value = null;

      if (videoCall) {
        videoCall.setIntentionalTrackStop(false);
      }
    } catch (err) {
      console.warn("stopCamera error", err);
    }
  }

  async function pauseVideo() {
    videoCall?.pauseProducer();
  }

  async function resumeVideo() {
    videoCall?.resumeProducer();
  }

  async function stopVideo() {
    try {
      await videoCall?.endCall();
      videoCall = null;

      cameraStream.value?.getTracks().forEach(t => t.stop());
      cameraStream.value = null;

      wasActivated.value = false;
    } catch (err) {
      console.warn("stopVideo error", err);
    }
  }

  return {
    loadVideo,
    switchFacingMode,
    stopVideo,
    startCamera,
    stopCamera,
    pauseVideo,
    resumeVideo,
    videoState,
    wasActivated,
    cameraWasEverGranted,
    cameraStream
  };
}
